import { useMemo, useState } from 'react';
import { useFamilies } from './hooks/useFamilies';
import { Menu, ActionIcon, Modal, Text, Button, Group, Box } from '@mantine/core';
import { IconDotsVertical, IconEdit, IconTrash } from '@tabler/icons-react';
import { useDeleteFamily } from './hooks/useDeleteFamily';
import { useNavigate } from 'react-router-dom';
import { useClientsContext } from './context/ClientsContext';
import { Family } from 'src/clients/hooks/useFamily';
import { DataTableColumn, DataTableColumnTextAlign } from 'mantine-datatable';
import CreateFamilyFlow from 'src/clients/CreateFamily/CreateFamilyFlow';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { useAdvisors } from './hooks/useAdvisors';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { formatName } from 'src/utils/nameFormatting';

export function FamiliesTable({ filters }: { filters?: Record<string, string> }) {
  const { refetch } = useFamilies(filters);
  const [familyToDelete, setFamilyToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const { deleteFamily } = useDeleteFamily();
  const navigate = useNavigate();
  const { editFamily } = useClientsContext();
  const { startFamilyCreation } = useClientsContext();
  const { advisors } = useAdvisors();
  const { isSystemAdmin, isServiceAdmin } = useCurrentUser();

  const advisorMap = useMemo(() => {
    if (!advisors) return {};

    return Object.fromEntries(advisors.map(advisor => [advisor.id, advisor]));
  }, [advisors]);

  const handleDelete = async () => {
    if (!familyToDelete) return;
    await deleteFamily(familyToDelete.id);
    setFamilyToDelete(null);
    refetch();
  };

  const columns: DataTableColumn<Family>[] = [
    {
      accessor: 'name',
      title: 'Family name',
      sortable: true,
      sortKey: 'name',
      textAlign: 'left' as DataTableColumnTextAlign,
    },
    {
      accessor: 'id',
      title: '# family members',
      render: (family: Family) => {
        return family.secondaryFamilyMembers.length + 1;
      },
    },
    {
      accessor: 'createdAt',
      title: 'Date added',
      sortable: true,
      sortKey: 'createdAt',
      textAlign: 'right' as DataTableColumnTextAlign,
      render: (family: Family) => {
        const date = new Date(family.createdAt);
        const formattedDate = date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
        return (
          <Text size="sm" c="gray.7">
            {formattedDate}
          </Text>
        );
      },
    },
    {
      accessor: 'actions',
      title: '',
      render: (family: { id: string; name: string }) => (
        <Box ta="right">
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => editFamily(family.id)}>
                Edit family
              </Menu.Item>
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => setFamilyToDelete(family)}
              >
                Delete family
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Box>
      ),
    },
  ];

  if (isSystemAdmin || isServiceAdmin) {
    columns.splice(2, 0, {
      accessor: 'advisor',
      title: 'Advisor',
      render: (family: Family) => {
        const advisorUserId = family.advisorUserId;
        if (!advisorUserId)
          return (
            <Text size="sm" c="gray.7">
              No Advisor
            </Text>
          );
        const advisor = advisorMap[advisorUserId];
        const advisorName = advisor
          ? formatName(advisor.firstName, advisor.lastName)
          : 'No Advisor';
        return (
          <Text size="sm" c="gray.7">
            {advisorName}
          </Text>
        );
      },
    });
  }

  return (
    <>
      <CreateFamilyFlow />
      <SimpleTable<Family>
        title="Families"
        resource="families"
        filters={filters}
        columns={columns}
        onRowClick={family => {
          navigate(`/clients/families/${family.id}`);
        }}
        primaryButton={{
          label: 'Add family',
          onClick: startFamilyCreation,
        }}
        enableSearch
      />
      <Modal
        opened={!!familyToDelete}
        onClose={() => setFamilyToDelete(null)}
        title="Delete family"
        centered
      >
        <Text mb="md">
          Are you sure you want to delete the {familyToDelete?.name} family? This will only delete
          the family, not the individual members. This action cannot be undone.
        </Text>
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={() => setFamilyToDelete(null)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete}>
            Delete
          </Button>
        </Group>
      </Modal>
    </>
  );
}
